// Story 7.10 — direct-to-blob image upload for text+image posts.
//
// Two-step flow (Story 1.21 pattern):
//   1. POST /api/v1/creators/me/posts/media/upload-url → signed write
//      URL + server-minted creator-scoped blob path.
//   2. PUT the file bytes straight to blob storage (XHR — fetch has no
//      upload-progress events) with the required Azure headers.
//
// Exposes upload(file) → { blob_path }, progress 0..100, error, reset.

import { useCallback, useRef, useState } from "react";

import { attachRequestIdToScope } from "../../lib/api-client";

const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp"];
export const MAX_MEDIA_BYTES = 5 * 1024 * 1024;

interface UploadUrlResponse {
  upload_url: string;
  blob_path: string;
  expires_at: string;
  required_headers: Record<string, string>;
}

function freshIdempotencyKey(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `idem-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function useUploadMediaBlob() {
  const [progress, setProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const activeXhr = useRef<XMLHttpRequest | null>(null);

  const reset = useCallback(() => {
    activeXhr.current?.abort();
    activeXhr.current = null;
    setProgress(0);
    setIsUploading(false);
    setError(null);
  }, []);

  const upload = useCallback(
    async (file: File): Promise<{ blob_path: string }> => {
      setError(null);
      setProgress(0);

      if (!ALLOWED_TYPES.includes(file.type)) {
        const message = "Only JPEG, PNG, or WebP images are supported.";
        setError(message);
        throw new Error(message);
      }
      if (file.size > MAX_MEDIA_BYTES) {
        const message = "Image is larger than 5 MB.";
        setError(message);
        throw new Error(message);
      }

      setIsUploading(true);
      try {
        const response = await fetch(
          "/api/v1/creators/me/posts/media/upload-url",
          {
            method: "POST",
            credentials: "include",
            headers: {
              "content-type": "application/json",
              "x-idempotency-key": freshIdempotencyKey(),
            },
            body: JSON.stringify({ content_type: file.type }),
          },
        );
        attachRequestIdToScope(response);
        if (!response.ok) {
          throw new Error(`upload URL mint failed (HTTP ${response.status})`);
        }
        const minted = (await response.json()) as UploadUrlResponse;

        await new Promise<void>((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          activeXhr.current = xhr;
          xhr.open("PUT", minted.upload_url);
          for (const [name, value] of Object.entries(
            minted.required_headers ?? {},
          )) {
            xhr.setRequestHeader(name, value);
          }
          xhr.upload.onprogress = (event) => {
            if (event.lengthComputable) {
              setProgress(Math.round((event.loaded / event.total) * 100));
            }
          };
          xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              setProgress(100);
              resolve();
            } else {
              reject(new Error(`blob upload failed (HTTP ${xhr.status})`));
            }
          };
          xhr.onerror = () => reject(new Error("blob upload failed (network)"));
          xhr.onabort = () => reject(new Error("blob upload cancelled"));
          xhr.send(file);
        });

        return { blob_path: minted.blob_path };
      } catch (e) {
        const message = e instanceof Error ? e.message : "upload failed";
        setError(message);
        throw e;
      } finally {
        activeXhr.current = null;
        setIsUploading(false);
      }
    },
    [],
  );

  return { upload, progress, isUploading, error, reset };
}
